"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"
import { StackingLayerProvider, useStackingLayer } from "@/components/ui/stacking-layer"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /**
   * Hide the top-right close (X) button. Use for dialogs that must not be
   * dismissible while a blocking operation is in flight (e.g. workflow Run
   * while tasks are being created). Pair with onEscapeKeyDown / onInteractOutside
   * preventDefault on the consumer side to fully lock the dialog.
   */
  hideClose?: boolean
  /**
   * Stacking layer for BOTH the overlay and the panel. Default `z-50` sits
   * above the page and a plain flyout, but the app stacks nested flyouts at
   * `z-[60]` (AssetDetailFlyout) and `z-[70]` (a preview opened from inside
   * one). A dialog raised from inside such a flyout has to clear it too —
   * otherwise it renders UNDER the panel it was opened from. That is invisible
   * on a wide screen (the flyout is a right-hand column, the dialog is centred
   * to its left) and total on a narrow one, where the flyout spans 92vw and
   * covers the whole dialog — the 2026-08-21 report.
   *
   * Pass the same token you would give the flyout's `className`, e.g. `z-[70]`.
   * Overlay and panel must move together, hence one prop instead of letting
   * callers patch `className` (which only reaches the panel).
   */
  zIndexClass?: string
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, hideClose = false, zIndexClass, ...props }, ref) => {
  const { t } = useTranslation('common')
  // 宿主声明的层(StackingLayerProvider)作兜底:调用点没显式给 zIndexClass 时,
  // 抬高的子树里的对话框也要跟着抬起来 —— 否则它停在 z-50,被 z-[60] 的 flyout
  // 整个盖住(2026-08-21 发布前审查发现:RecognizeNameplateDialog、
  // EditableAttachments 的删除确认/视频播放都是这条路径)。
  const layer = useStackingLayer()
  const effectiveLayer = zIndexClass ?? layer
  return (
    <DialogPortal>
      <DialogOverlay className={effectiveLayer} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          // context 层放在 className 之前:调用点显式写的 z 仍然赢(见 stacking-layer 注释)
          layer,
          className,
          zIndexClass
        )}
        {...props}
      >
        {/* 对话框自身就是新的层宿主:它里面开的 Select / Popover / 下拉 / 二次确认
            必须至少和它同层,否则会被自己的面板盖住(ChangeStatus 的状态下拉即此) */}
        <StackingLayerProvider value={effectiveLayer}>{children}</StackingLayerProvider>
        {!hideClose && (
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <X className="h-4 w-4" />
            <span className="sr-only">{t('ui.dialog.close')}</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
